import { estApiError } from '../api/errors.js';
import type { SessionUtilisateur } from '../api/types.js';
import { cheminConditions, estRefusCgu } from './cgu.js';
import { retourSur } from './retour.js';

/**
 * Session CONFINÉE : l'API ne lui sert plus que l'écran qui la libère. Deux
 * cas, dans cet ordre — l'API refuse d'abord pour les CGU :
 *
 *  1. les CGU sont à accepter (ARTO-59, `cguAcceptationRequise`) :
 *     `/app/conditions` ;
 *  2. la grâce pour activer la double authentification est échue
 *     (`enrolementMfaSeulement`) : `/app/double-authentification`.
 *
 * La régie en tire trois conséquences :
 *
 *  - les chargeurs qui exigent la session mènent à la page qui libère, avec
 *    un retour (`routes/loaders.ts`) ; un refus pour l'un de ces motifs reçu
 *    en cours d'usage fait revalider la coquille, qui y mène de même ;
 *  - AUCUN repli hors ligne n'ouvre la régie à un compte dont la dernière
 *    session vue était confinée (`horsLigne/index.ts`,
 *    `routes/racine/horsLigne.ts`). L'instantané est effacé à la vue d'une
 *    telle session ; ce repère tient en mémoire si l'effacement a échoué.
 *    Il survit au rechargement seulement si le stockage local l'a conservé.
 *    Si stockage local ET effacement échouent, la recharge perd le repère :
 *    un instantané ancien encore lisible peut alors permettre le repli ;
 *  - le rejeu du stand s'arrête sans rien imputer aux actions en attente.
 */

/** Vrai quand l'API ne sert plus à cette session que l'écran qui la libère. */
export function estConfinee(session: SessionUtilisateur): boolean {
  return Boolean(session.cguAcceptationRequise || session.enrolementMfaSeulement);
}

/** `/app/double-authentification?retour=…`, le retour assaini comme partout. */
export function cheminDoubleAuthentification(retour: string): string {
  return `/app/double-authentification?retour=${encodeURIComponent(retourSur(retour))}`;
}

/** La page qui libère une session confinée, CGU d'abord ; `null` si elle est libre. */
export function cheminDeLiberation(session: SessionUtilisateur, retour: string): string | null {
  if (session.cguAcceptationRequise) return cheminConditions(retour);
  if (session.enrolementMfaSeulement) return cheminDoubleAuthentification(retour);
  return null;
}

/** Le refus de l'API pour une session confinée à l'enrôlement. */
export function estRefusEnrolementMfa(erreur: unknown): boolean {
  if (!estApiError(erreur) || erreur.status !== 403) return false;
  return (erreur.details as { raison?: unknown } | null)?.raison === 'mfa_enrolement_requis';
}

/** Le refus de l'API pour une session confinée, quel qu'en soit le motif. */
export function estRefusConfinement(erreur: unknown): boolean {
  return estRefusCgu(erreur) || estRefusEnrolementMfa(erreur);
}

// ── Ce que la dernière session vue exigeait ─────────────────────────────────

// Nom historique (ARTO-59, CGU seules) gardé : un repère déjà posé vaut encore.
const CLE_STOCKAGE = 'artosera-cgu-a-accepter';
let compteEnAttente: string | null = null;

function lireStockage(): string | null {
  try {
    return localStorage.getItem(CLE_STOCKAGE);
  } catch {
    return null;
  }
}

function ecrireStockage(compteId: string | null): void {
  try {
    if (compteId === null) localStorage.removeItem(CLE_STOCKAGE);
    else localStorage.setItem(CLE_STOCKAGE, compteId);
  } catch {
    // Stockage indisponible : le repère en mémoire tient pour cet onglet.
  }
}

/**
 * Note ce qu'une session fraîchement relue du SERVEUR exige. Une session de
 * consultation hors ligne ne prouve rien : elle est ignorée.
 */
export function noterConfinement(session: SessionUtilisateur): void {
  if (session.consultationHorsLigne) return;
  const compteId = session.compte.id;
  if (estConfinee(session)) {
    compteEnAttente = compteId;
    ecrireStockage(compteId);
    return;
  }
  if (compteEnAttente === compteId) compteEnAttente = null;
  if (lireStockage() === compteId) ecrireStockage(null);
}

/**
 * Un refus pour confinement vient d'arriver : le repli hors ligne se ferme
 * pour ce compte AVANT la relecture de la session, qui peut échouer faute de
 * réseau. Seule une session relue et libre le rouvre (`noterConfinement`).
 */
export function marquerConfine(compteId: string): void {
  compteEnAttente = compteId;
  ecrireStockage(compteId);
}

/** Vrai quand la dernière session vue de ce compte était confinée. */
export function compteConfine(compteId: string): boolean {
  return compteEnAttente === compteId || lireStockage() === compteId;
}

/** Tests seulement. */
export function oublierConfinement(): void {
  compteEnAttente = null;
  ecrireStockage(null);
}

// ── Annonce en cours d'usage ─────────────────────────────────────────────────

const abonnes = new Set<() => void>();

/**
 * Un refus pour confinement est arrivé : la coquille revalide sa route, et la
 * session RELUE décide — elle mène à la page qui la libère si elle est
 * toujours confinée, sous l'identité qu'elle porte (`docs/dev/mode-foire.md` § 1).
 */
export function surConfinement(rappel: () => void): () => void {
  abonnes.add(rappel);
  return () => abonnes.delete(rappel);
}

export function annoncerConfinement(): void {
  for (const rappel of abonnes) rappel();
}
